import { useState, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Home } from '@lmc/ts-common'
import {
  PageHeader,
  Button,
  Spinner,
  FilterContainer,
  RangePicker,
  MultiSelect
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { PlusOutlined, FilePdfOutlined } from '@ant-design/icons'
import { Tabs } from 'antd'
import { NAMESPACE } from 'locale'
import useResident from 'hooks/useResident'
import usePdfExport from 'hooks/usePdfExport'
import PageWrapper from '../componentsV2/PageWrapper'
import Operations from '../../../../../operations'
import Actions from '../../../../../actions'
import Legend from './Legend'
import MedicationsTable from './MedicationsTable'
import { StockRecordDrawer } from '../EMARMedicationStockScreenV2/StockRecordDrawer/StockRecordDrawer'
import { StockRecord } from '../types'
import { MedicationStockReason } from '../types'
import { useMarChart } from '../hooks/useMarChart'
import { transformMarChartData } from '../utils/administrationUtils'

const EMARResidentScreenV2 = () => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const { id: residentId } = useParams<{ id: string }>()
  const dispatch = useDispatch()
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const user = useSelector(
    (state: { global: { user?: { medicationPermission?: boolean } } }) =>
      state.global.user || { medicationPermission: true }
  )

  const { resident, isLoading: isLoadingResident } = useResident({
    id: residentId
  })

  const [startDate, setStartDate] = useState(
    dayjs().subtract(14, 'days').startOf('day')
  )
  const [endDate, setEndDate] = useState(dayjs().endOf('day'))

  const { data: marChartData, isLoading: isLoadingMarChart } = useMarChart({
    residentId: residentId || '',
    startDate,
    endDate,
    enabled: !!residentId
  })

  const [selectedMedications, setSelectedMedications] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState<string>('scheduled')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<
    StockRecord | undefined
  >()
  const [selectedMedicationName, setSelectedMedicationName] =
    useState<string>('')
  const [isDownloading, setIsDownloading] = useState(false)
  const pdfContentRef = useRef<HTMLDivElement>(null)

  const medicationOptions = useMemo(() => {
    if (!marChartData?.medications) return []
    return marChartData.medications.map((med: any) => ({
      value: med.residentMedicationId,
      label: med.medicationName
    }))
  }, [marChartData])

  const scheduledTableData = useMemo(() => {
    const data = transformMarChartData(
      marChartData,
      'scheduled',
      startDate,
      endDate
    )
    if (selectedMedications.length === 0) return data
    return data.filter((row) => {
      return selectedMedications.includes(row.residentMedicationId)
    })
  }, [marChartData, startDate, endDate, selectedMedications])

  const prnTableData = useMemo(() => {
    const data = transformMarChartData(marChartData, 'prn', startDate, endDate)
    if (selectedMedications.length === 0) return data
    return data.filter((row) => {
      return selectedMedications.includes(row.residentMedicationId)
    })
  }, [marChartData, startDate, endDate, selectedMedications])

  const isInitialAssessment =
    resident?.status === 'pre-assessment' || resident?.isActiveInitialAssessment

  const residentName = resident
    ? `${resident.name.first} ${resident.name.last}`
    : ''

  const handleAddMedication = () => {
    if (!resident) return
    dispatch(Operations.openModal({ modalId: 'addEmarMedication' }))
    dispatch(
      Actions.addForm({
        formId: 'addEmarMedication',
        initial: { resident: resident.id }
      })
    )
  }

  const handleDateChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setStartDate(dates[0].startOf('day'))
      setEndDate(dates[1].endOf('day'))
    } else {
      setStartDate(dayjs().subtract(14, 'days').startOf('day'))
      setEndDate(dayjs().endOf('day'))
    }
  }

  const handleMedicationChange = (medications: string[]) => {
    setSelectedMedications(medications)
  }

  const handleTabChange = (key: string) => {
    setActiveTab(key)
  }

  const handleCellClick = (record: any, dateKey: string) => {
    const cellData = record[dateKey]
    if (!cellData?.outcomes || cellData.outcomes.length === 0) return

    const firstOutcome = cellData.outcomes[0]
    if (firstOutcome.action !== 'ADMINISTERED') return

    const recordDetails = firstOutcome.recordDetails || {}
    const medication = record.medication

    const stockRecord: StockRecord = {
      id: `${record.key}-${dateKey}`,
      date:
        recordDetails.timeRecorded ||
        dayjs(dateKey, 'DD-MM-YYYY').toISOString(),
      action: MedicationStockReason.MED_ADMIN,
      recordedBy: {
        id: recordDetails.recordedById || '',
        name: recordDetails.recordedBy || '',
        picture: recordDetails.recordedByPicture
      },
      notes: recordDetails.comment || recordDetails.comments || [],
      quantityChange: recordDetails.quantity
        ? -parseInt(recordDetails.quantity)
        : -parseInt(record.dose) || 0,
      unit: 'tablets',
      witnessed: !!recordDetails.witnessedBy,
      witnesses: recordDetails.witnessedBy
        ? [
            {
              userId: recordDetails.witnessedById || '',
              witnessedAt: recordDetails.timeRecorded || dayjs().toISOString()
            }
          ]
        : [],
      expectedStock: medication?.currentStock || 0,
      stockCount: medication?.currentStock || 0
    } as StockRecord

    setSelectedRecord(stockRecord)
    setSelectedMedicationName(medication?.name || '')
    setDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setSelectedRecord(undefined)
    setSelectedMedicationName('')
  }

  const pdfTitle = `${residentName}'s ${t('EMAR')} - ${startDate.format('DD-MM-YYYY')} to ${endDate.format('DD-MM-YYYY')}`
  const handleDownloadPdf = usePdfExport(
    pdfContentRef as React.RefObject<HTMLInputElement>,
    pdfTitle,
    setIsDownloading
  )

  const tabItems = [
    {
      key: 'scheduled',
      label: <Text>{t('SCHEDULED')}</Text>,
      children: (
        <MedicationsTable
          startDate={startDate}
          endDate={endDate}
          type="scheduled"
          residentFirstName={resident?.name?.first || ''}
          onCellClick={handleCellClick}
          data={scheduledTableData}
        />
      )
    },
    {
      key: 'prn',
      label: <Text>{t('PRN')}</Text>,
      children: (
        <MedicationsTable
          startDate={startDate}
          endDate={endDate}
          type="prn"
          residentFirstName={resident?.name?.first || ''}
          onCellClick={handleCellClick}
          data={prnTableData}
        />
      )
    }
  ]

  if (isLoadingResident || !resident || isLoadingMarChart) {
    return (
      <PageWrapper>
        <Spinner />
      </PageWrapper>
    )
  }

  return (
    <>
      <PageHeader title={`${residentName}'s ${t('EMAR')}`} sticky>
        <HeaderButtonContainer>
          <Button
            onClick={handleDownloadPdf}
            variant="default"
            isLoading={isDownloading}
            data-testid="emar-download-pdf-button"
            style={{ whiteSpace: 'nowrap' }}
          >
            <FilePdfOutlined />
            {t('DOWNLOAD_PDF')}
          </Button>
          {user.medicationPermission && !isInitialAssessment && (
            <Button
              id="add-medication-button"
              onClick={handleAddMedication}
              variant="primary"
              disabled={home && !home.features?.emarOnboarded?.enabled}
              style={{ whiteSpace: 'nowrap' }}
            >
              <PlusOutlined />
              {t('ADD_MEDICATION')}
            </Button>
          )}
        </HeaderButtonContainer>
      </PageHeader>
      <PageWrapper>
        <FilterContainer title="">
          <>
            <RangePicker
              defaultValue={[startDate, endDate]}
              value={[startDate, endDate]}
              onChange={handleDateChange}
              format="DD-MM-YYYY"
              data-testid="emar-date-range-picker"
            />
            <MultiSelect
              title={t('MEDICATION')}
              options={medicationOptions}
              values={selectedMedications}
              onChange={handleMedicationChange}
              hasClear
              hasSearch
              hasSelectAll
              data-testid="emar-medication-filter"
            />
          </>
        </FilterContainer>
        <div ref={pdfContentRef}>
          <StyledTabs
            defaultActiveKey="scheduled"
            activeKey={activeTab}
            items={tabItems}
            onChange={handleTabChange}
            tabBarExtraContent={<Legend />}
          />
        </div>
        <StockRecordDrawer
          isVisible={drawerOpen}
          onClose={handleCloseDrawer}
          record={selectedRecord}
          medicationName={selectedMedicationName}
          controlledDrug={false}
        />
      </PageWrapper>
    </>
  )
}

const HeaderButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`

const StyledTabs = styled(Tabs)`
  flex: 1;
`

export default EMARResidentScreenV2
